export const SECTORS_CODES = ['CSD', 'IFD', 'INE', 'PTI', 'SCL'];

export interface CompositionSector {
  region: string;
  data: Record<string, number>;
  projectCounts?: Record<string, number>;
}

export const formatCompositionNumber = (value: number, minimumFractionDigits = 1) =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits,
    maximumFractionDigits: Math.max(minimumFractionDigits, 12),
  }).format(value);

export const formatCompositionAmount = (value: number) =>
  formatCompositionNumber(Number(value.toFixed(1)));

export const createPdfScale = (maxValue: number) => {
  if (maxValue <= 0) return { max: 0, ticks: [0] };

  const rawStep = maxValue / 6;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalizedStep = rawStep / magnitude;
  const stepMultiplier = normalizedStep <= 1 ? 1 : normalizedStep <= 2 ? 2 : normalizedStep <= 5 ? 5 : 10;
  const step = stepMultiplier * magnitude;
  const tickCount = Math.ceil(maxValue / step);
  const ticks = Array.from({ length: tickCount + 1 }, (_, index) => Number((index * step).toPrecision(12)));
  return { max: ticks[tickCount], ticks };
};

export const prepareComposition = (sectors: CompositionSector[], colors: string[]) => {
  let total = 0;
  let max = 0;
  const prepared = sectors.filter(sector => SECTORS_CODES.includes(sector.region)).map((sector, index) => {
    let sectorTotal = 0;
    const divisions = Object.entries(sector.data ?? {}).map(([label, value]) => {
      const amount = Number.isFinite(value) ? value : 0;
      sectorTotal += amount;
      max = Math.max(max, amount);
      return { label, amount, count: Number(sector.projectCounts?.[label] ?? 0) };
    });
    total += sectorTotal;
    return { region: sector.region, color: colors[index], total: sectorTotal, divisions };
  });
  return {
    total,
    scale: createPdfScale(max),
    sectors: prepared.map(sector => ({ ...sector, percent: total > 0 ? sector.total / total * 100 : 0 })),
  };
};

export const isMinibizableGrid = (sectors, isTCP = true) => {
  const drawableSectors = sectors.filter(sector => SECTORS_CODES.includes(sector.region));
  const drawableDivisions = drawableSectors.reduce((acc, sector) => acc + Object.keys(sector.data ?? {}).length, 0);

  const limitSectors = isTCP ? 4 : 3;
  const limitDivisions = isTCP ? 8 : 6;

  return drawableSectors.length <= limitSectors || drawableDivisions <= limitDivisions;
}
